import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAfter, subMinutes } from 'date-fns';
import { MailIcon, MessageSquareIcon, SmileIcon, LucideIcon } from 'lucide-react';

import dayjs from 'utils/extendedDayJS';
import { Button } from '@epoch-ui/components/Button';
import { ErrorMessage } from '@epoch-ui/components/ErrorMessage';
import { Form, DateTimePickerField } from '@epoch-ui/components/Form';
import { ButtonLink } from '@epoch-ui/components/ButtonLink';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter,
  ModalDescription
} from '@epoch-ui/components/Modal/modal';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import {
  useSchedulePublishModal,
  usePublishErrorsModal
} from '@event-editor/hooks/eventEditorState';
import { validatePublish } from '@event-editor/components/EventEditorHeader';
import { calculateInitialDateTimeSettings } from 'pages/dashboard/overview/events/create/utils';
import {
  convertSendAtToFormValue,
  convertSendAtToDBValue
} from 'pages/dashboard/overview/events/schemas/commonUtils';
import { TimezoneSelectorField } from 'pages/dashboard/overview/events/components/TimezoneSelectorField';
import {
  getStartOfDayInTimezone,
  interpretDateInTimezone,
  guessTimezone
} from 'utils/dateTimeUtils';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { scheduledPublishApi } from '@event-editor/api/scheduled-publish';
import { ScheduleImpactCard } from '@event-editor/components/ScheduleImpactCard';
import {
  getScheduledCommunicationConflicts,
  ScheduledCommunicationConflict
} from '@event-editor/utils';

import {
  SchedulePublishModalFormValues,
  SchedulePublishModalFormInput,
  createSchedulePublishModalSchema,
  convertSchedulePublishDBToFormData
} from './SchedulePublishModal.schema';

export const SchedulePublishModal: React.FC = () => {
  const event = useEditableEvent();
  const { isOpen, isReschedule, close } = useSchedulePublishModal();
  const { showBanner: showPublishErrorsBanner, dismissBanner: dismissPublishErrorsBanner } =
    usePublishErrorsModal();
  const { showToast } = useToaster();
  const queryClient = useQueryClient();

  const [conflictsModalOpen, setConflictsModalOpen] = useState(false);

  const { datetimeScheduled, publishSchedule } = event;
  const eventTimezone = event.timezoneCode ?? guessTimezone();

  const defaultValues = useMemo((): SchedulePublishModalFormInput => {
    // If rescheduling, use the existing scheduled time and timezone
    if (isReschedule && publishSchedule?.scheduledAt) {
      const scheduleTimezone = publishSchedule.timezoneCode ?? eventTimezone;
      const existing = convertSchedulePublishDBToFormData(
        publishSchedule.scheduledAt,
        scheduleTimezone
      );
      if (existing) {
        return existing;
      }
    }

    // Default to 10 minutes before event start time if available, otherwise use a reasonable default
    const defaultDate = datetimeScheduled
      ? subMinutes(convertSendAtToFormValue(datetimeScheduled, eventTimezone), 10)
      : calculateInitialDateTimeSettings().startDateTime;

    return {
      scheduledAt: defaultDate,
      timezoneCode: eventTimezone
    };
  }, [
    isReschedule,
    publishSchedule?.scheduledAt,
    publishSchedule?.timezoneCode,
    datetimeScheduled,
    eventTimezone
  ]);

  const form = useForm<SchedulePublishModalFormInput, unknown, SchedulePublishModalFormValues>({
    resolver: zodResolver(
      createSchedulePublishModalSchema({
        datetimeScheduled
      })
    ),
    values: defaultValues,
    mode: 'onChange'
  });

  const { handleSubmit, reset, watch } = form;

  const selectedTimezone = watch('timezoneCode');
  const watchedScheduledAt = watch('scheduledAt');

  // Calculate communications conflicts based on watched form values
  const communicationConflicts = useMemo((): ScheduledCommunicationConflict[] => {
    if (!watchedScheduledAt || !selectedTimezone || form.formState.errors.scheduledAt?.message) {
      return [];
    }

    const publishAtUTC = convertSendAtToDBValue(watchedScheduledAt, selectedTimezone);

    return getScheduledCommunicationConflicts({
      slackReminders: event.slackReminders,
      emailReminders: event.emailReminders,
      feedbackSchedule: event.feedbackSchedule,
      feedbackEnable: event.feedbackEnable,
      datetimeScheduled,
      datetimeEnd: event.datetimeEnd,
      publishAtUTC
    });
  }, [
    form.formState.errors.scheduledAt?.message,
    watchedScheduledAt,
    selectedTimezone,
    event.slackReminders,
    event.emailReminders,
    event.feedbackSchedule,
    event.feedbackEnable,
    datetimeScheduled,
    event.datetimeEnd
  ]);

  const hasCommunicationConflicts = communicationConflicts.length > 0;

  // Schedule mutation
  const scheduleMutation = useMutation({
    mutationFn: ({
      eventId,
      scheduledAt,
      timezoneCode
    }: {
      eventId: number;
      scheduledAt: string;
      timezoneCode?: string;
    }) => scheduledPublishApi.schedule(eventId, scheduledAt, timezoneCode),
    onSuccess: (result) => {
      if (result.ok) {
        queryClient.setQueryData(eventEditorQueryKeys.event(event.id!), (oldData: any) =>
          oldData
            ? {
                event: {
                  ...oldData.event,
                  publishSchedule: result.schedule
                }
              }
            : oldData
        );
        showToast('Event is scheduled for publishing');
        close();
        reset();
      } else {
        showToast('Event could not be scheduled for publishing, please try again later.', {
          variant: ToasterVariant.ERROR
        });
      }
    },
    onError: () => {
      showToast('Event could not be scheduled for publishing, please try again later.', {
        variant: ToasterVariant.ERROR
      });
    }
  });

  // Reschedule mutation
  const rescheduleMutation = useMutation({
    mutationFn: ({
      eventId,
      scheduledAt,
      timezoneCode
    }: {
      eventId: number;
      scheduledAt: string;
      timezoneCode?: string;
    }) => scheduledPublishApi.reschedule(eventId, scheduledAt, timezoneCode),
    onSuccess: (result) => {
      if (result.ok) {
        queryClient.setQueryData(eventEditorQueryKeys.event(event.id!), (oldData: any) =>
          oldData
            ? {
                event: {
                  ...oldData.event,
                  publishSchedule: result.schedule
                }
              }
            : oldData
        );
        showToast('Event publish rescheduled successfully.');
        close();
        reset();
      } else {
        showToast('Failed to reschedule event publish.', { variant: ToasterVariant.ERROR });
      }
    },
    onError: () => {
      showToast('Failed to reschedule event publish.', { variant: ToasterVariant.ERROR });
    }
  });

  const isPending = scheduleMutation.isPending || rescheduleMutation.isPending;

  // Calculate date constraints for the date picker (with timezone consideration)
  const getCalendarDateDisabled = useMemo(() => {
    return (date: Date): boolean => {
      // Convert the calendar date to the selected timezone
      const dateInTimezone = interpretDateInTimezone(date, selectedTimezone);

      // Disable if after event start date
      if (datetimeScheduled) {
        const eventStartDay = getStartOfDayInTimezone(selectedTimezone, datetimeScheduled);
        if (isAfter(dateInTimezone, eventStartDay)) {
          return true;
        }
      }

      return false;
    };
  }, [selectedTimezone, datetimeScheduled]);

  const handleSave = handleSubmit(
    (data: SchedulePublishModalFormValues): void => {
      if (!event?.id) return;

      // Clear stale banner state, then check for expired features
      dismissPublishErrorsBanner();
      const expiredErrors = validatePublish(event);
      if (expiredErrors.length > 0) {
        showPublishErrorsBanner(expiredErrors);
        close();
        reset();
        return;
      }

      if (isReschedule) {
        rescheduleMutation.mutate({
          eventId: event.id,
          scheduledAt: data.scheduledAt,
          timezoneCode: data.timezoneCode
        });
      } else {
        scheduleMutation.mutate({
          eventId: event.id,
          scheduledAt: data.scheduledAt,
          timezoneCode: data.timezoneCode
        });
      }
    },
    (errors) => {
      console.error(errors);
    }
  );

  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      close();
      reset();
    }
  };

  // Get the icon for a conflict type
  const getConflictIcon = (type: ScheduledCommunicationConflict['type']): LucideIcon => {
    switch (type) {
      case 'slack_reminder':
        return MessageSquareIcon;
      case 'email_reminder':
        return MailIcon;
      case 'feedback':
        return SmileIcon;
      default:
        return MessageSquareIcon;
    }
  };

  // Handle "See details" link click
  const handleSeeDetailsClick = (): void => {
    setConflictsModalOpen(true);
  };

  return (
    <>
      <Modal open={isOpen} onOpenChange={handleOpenChange}>
        <Form {...form}>
          <ModalContent className="tw-max-w-[400px]">
            <form onSubmit={handleSave}>
              <ModalHeader className="tw-mb-5">
                <ModalTitle className="tw-text-2xl tw-font-medium">
                  {isReschedule ? 'Reschedule publish' : 'Schedule publish'}
                </ModalTitle>
              </ModalHeader>
              <ModalDescription className="tw-sr-only">
                {isReschedule
                  ? 'Update when this event will be automatically published.'
                  : 'Set a date and time when this event will be automatically published.'}
              </ModalDescription>

              <DateTimePickerField name="scheduledAt">
                <div className="tw-flex tw-w-full tw-flex-col tw-gap-2">
                  {/* Date picker - full width */}
                  <DateTimePickerField.DateInput
                    disabled={getCalendarDateDisabled}
                    className="tw-w-full [&_input]:tw-max-w-none [&_input]:tw-flex-1"
                  />

                  {/* Time and Timezone - side by side */}
                  <div className="tw-flex tw-gap-3">
                    <DateTimePickerField.Time interval={5} className="tw-flex-1" />
                    <TimezoneSelectorField
                      name="timezoneCode"
                      valueFormat="abbreviation"
                      className="tw-flex-1"
                    />
                  </div>

                  {/* Custom error message for communications conflict */}
                  {hasCommunicationConflicts && (
                    <ErrorMessage>
                      Publish time must be before scheduled communications.{' '}
                      <ButtonLink
                        onClick={handleSeeDetailsClick}
                        className="tw-text-xs tw-font-normal tw-underline"
                      >
                        See details
                      </ButtonLink>
                    </ErrorMessage>
                  )}
                </div>
              </DateTimePickerField>

              <ModalFooter className="tw-mt-5">
                <Button
                  size="sm"
                  className="tw-w-full tw-rounded-lg"
                  type="submit"
                  variant="primary"
                  disabled={isPending || hasCommunicationConflicts}
                  loading={isPending}
                >
                  {isReschedule ? 'Reschedule' : 'Schedule'}
                </Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Form>
      </Modal>

      {/* Conflicts Details Modal */}
      <Modal open={conflictsModalOpen} onOpenChange={setConflictsModalOpen}>
        <ModalContent className="tw-max-w-[560px]">
          <ModalHeader className="tw-mb-4">
            <ModalTitle className="tw-text-xl tw-font-medium">Scheduled features</ModalTitle>
          </ModalHeader>
          <ModalDescription className="tw-text-content-secondary tw-mb-4 tw-text-sm">
            This event can&apos;t be published for scheduling because the scheduled time of the
            following features is before the scheduled publish time for this event. Update the
            scheduled time of these features or update the publish time of this event and try again.
          </ModalDescription>
          <div className="tw-flex tw-flex-col tw-gap-3">
            {communicationConflicts.map((conflict, index) => (
              <ScheduleImpactCard
                key={`${conflict.type}-${index}`}
                icon={getConflictIcon(conflict.type)}
                heading={conflict.name}
                description={
                  <>
                    Scheduled time:{' '}
                    {dayjs.formatPrettyDateTime({
                      dateTime: conflict.scheduledAt,
                      timezoneCode: eventTimezone
                    })}
                    <br />
                    <span className="tw-text-destructive-foreground">
                      Scheduled time can&apos;t be before the event is published
                    </span>
                  </>
                }
              />
            ))}
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};
